import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import Notification from 'components/Notification';
import { Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import rolesService from 'utils/network/roles/roles.service';
import roleAccessService from 'utils/network/role-access/role-access.service';
import { SHOW_ALL_LIMIT, SUCCESS_MESSAGE } from 'utils/constants';

const initialValues = {
  id: null,
  name: '',
  accesses: []
};

const PAGINATION = {
  DEFAULT_KEYWORD: '',
  DEFAULT_PAGE: 1,
  DEFAULT_PER_PAGE: 10,
  DEFAULT_TOTAL_PAGE: 10,
  DEFAULT_TOTAL_DATA: 0
};

export default function Roles() {
  const columns = [
    {
      name: 'Name',
      sortable: true,
      selector: 'name',
      cell: function render(row) {
        return <>{row.name}</>;
      }
    },
    {
      name: 'Actions',
      cell: function render(row) {
        return (
          <div className="w-full">
            <button
              className="p-1 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleCreateEdit(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              className="p-1 ml-2 text-center bg-gray-100 border rounded-lg leading-5 transition-colors duration-150 active:bg-purple-600 hover:bg-gray-300 focus:outline-none focus:shadow-outline-gray"
              onClick={handleDelete(row)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        );
      }
    }
  ];

  const [pagination, setPagination] = useState({
    search: PAGINATION.DEFAULT_KEYWORD,
    page: PAGINATION.DEFAULT_PAGE,
    perPage: PAGINATION.DEFAULT_PER_PAGE,
    totalPage: PAGINATION.DEFAULT_TOTAL_PAGE,
    totalData: PAGINATION.DEFAULT_TOTAL_DATA
  });
  const [form, setForm] = useState(initialValues);
  const [createEdit, setCreateEdit] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    error: false,
    title: '',
    message: ''
  });
  const [showDelete, setShowDelete] = useState(false);
  const [sortProp, setSortProp] = useState({
    order: 'created_at',
    sort: 'desc'
  });

  const query = useQueryClient();
  const queryKey = useMemo(() => {
    return `role-list--${JSON.stringify({
      page: pagination.page,
      perPage: pagination.perPage,
      search: pagination.search,
      sortProp
    })}`;
  }, [pagination.page, pagination.perPage, pagination.search, sortProp]);

  const roleAccessQueryKey = useMemo(() => {
    return `role-access-list--${JSON.stringify({
      page: PAGINATION.DEFAULT_PAGE,
      perPage: SHOW_ALL_LIMIT,
      search: PAGINATION.DEFAULT_KEYWORD
    })}`;
  }, []);

  // get list role access
  const { data: roleAccessData } = useQuery({
    roleAccessQueryKey,
    queryFn: () => {
      return roleAccessService.list({
        limit: SHOW_ALL_LIMIT,
        access_name: '',
        offset: PAGINATION.DEFAULT_PAGE,
        order: 'created_at',
        sort: 'desc'
      });
    }
  });

  // get list
  const { data, isLoading: getRolesLoading } = useQuery({
    queryKey,
    queryFn: () => {
      return rolesService.list({
        limit: pagination.perPage,
        tier_name: pagination.search,
        offset: pagination.page,
        order: sortProp.order,
        sort: sortProp.sort
      });
    }
  });

  // create or update
  const { mutate: createUpdateRole } = useMutation(
    (body) => {
      if (body.id) {
        return rolesService.put(body);
      }
      return rolesService.post(body);
    },
    {
      onSuccess: (data) => {
        if (data?.data?.message === SUCCESS_MESSAGE) {
          setNotification({
            ...notification,
            show: true,
            success: true,
            title: 'Success',
            message: `Role successfully ${form?.id ? 'updated' : 'created'}`
          });
          query.invalidateQueries([queryKey]);
          closeCreateEdit();
        }
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: 'Error create role'
        });
      }
    }
  );

  // delete
  const { mutate: deleteRole } = useMutation(
    (id) => {
      return rolesService.delete(id);
    },
    {
      onSuccess: (data) => {
        if (data?.data?.message === SUCCESS_MESSAGE) {
          query.invalidateQueries([queryKey]);
          closeDelete();
          setNotification({
            ...notification,
            success: true,
            show: true,
            title: 'Success',
            message: 'Role successfully deleted'
          });
        }
      },
      onError: () => {
        setNotification({
          ...notification,
          show: true,
          title: 'Error',
          error: true,
          message: 'Error delete role'
        });
      }
    }
  );

  function handlePerRowsChange(newPerPage, page) {
    setPagination({
      ...pagination,
      page,
      perPage: newPerPage,
      totalPage: Math.ceil(roleAccessData?.data?.total_page / newPerPage)
    });
  }

  function handlePageChange(page) {
    setPagination({
      ...pagination,
      page
    });
  }

  const handleCreateEdit = (row) => () => {
    if (row)
      setForm({
        ...form,
        name: row.name,
        id: row.id,
        accesses: row.access
      });
    setCreateEdit(true);
  };

  function closeCreateEdit() {
    setCreateEdit(false);
  }

  const handleDelete = (row) => () => {
    setForm({ ...form, id: row.id });
    setShowDelete(true);
  };

  function closeDelete() {
    setShowDelete(false);
  }

  function handleChangeForm(e) {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }

  const handleSort = useCallback((column, sortDirection) => {
    setSortProp({
      order: column.selector,
      sort: sortDirection
    });
  });

  function notif(title, message, isError = false, duration = 3000) {
    setNotification({
      show: true,
      title: title,
      message: message,
      error: isError
    });

    setTimeout(() => {
      setNotification({ ...notification, show: false });
    }, duration);
  }

  function closeNotification() {
    setNotification({ ...notification, show: false });
  }

  function isValid() {
    return form.name.length > 0;
  }

  useEffect(() => {
    if (!createEdit && !showDelete) setForm(initialValues);
  }, [createEdit, showDelete]);

  async function handleCreateEditRole() {
    if (!isValid()) {
      notif('Error', 'Please fill all required field!', true);
      return;
    }

    try {
      const body = {
        ...form,
        access: form.accesses
          .map((access) => ({
            access_name: access?.access_name
          }))
          .filter((x) => !!x.access_name)
      };
      delete body.accesses;

      if (form.id) {
        createUpdateRole(body);
      } else {
        delete body.id;

        createUpdateRole(body);
      }
    } catch (error) {
      notif('Error', 'Network error. Please try again later!', true);
    }
  }

  async function handleDeleteRole() {
    deleteRole(form?.id);
  }

  return (
    <div>
      <div className="flex flex-row items-center justify-between">
        <h2 className="my-6 text-xl font-semibold text-gray-700 dark:text-gray-200">
          Roles
        </h2>
        <button
          className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
          onClick={handleCreateEdit(null)}
        >
          Add New
        </button>
      </div>
      <DataTable
        columns={columns}
        data={data?.data || []}
        responsive
        sortServer
        onSort={handleSort}
        progressPending={getRolesLoading}
        pagination
        paginationServer
        paginationTotalRows={pagination.totalData}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
      />

      {/* Create Edit Section */}
      <Transition
        show={createEdit}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

            <div
              className="inline-block px-8 pt-5 pb-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              {form.id ? (
                <h3 className="mb-3 font-bold">Edit Role</h3>
              ) : (
                <h3 className="mb-3 font-bold">Create Role</h3>
              )}
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeCreateEdit}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Name <span className="text-red-500">*</span>
                </span>
                <input
                  className="block w-full px-3 py-2 mt-1 text-sm border appearance-none rounded-md dark:border-gray-600 dark:bg-gray-700 focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:text-gray-300 dark:focus:shadow-outline-gray form-input"
                  placeholder="Administrator"
                  name="name"
                  value={form.name}
                  onChange={handleChangeForm}
                />
              </label>
              <label className="block mt-3 text-sm">
                <span className="text-gray-700 dark:text-gray-400">
                  Access <span className="text-red-500">*</span>
                </span>
                <div className="mt-3">
                  {!!roleAccessData?.data &&
                    roleAccessData?.data?.map((roleAccess) => {
                      return (
                        <div
                          key={`role-access--${roleAccess?.id}`}
                          className="flex flex-row gap-3 border rounded mb-3 p-3"
                        >
                          <input
                            type="checkbox"
                            // value={roleAccess?.access_name}
                            checked={
                              form.accesses?.findIndex(
                                (x) => x.access_name === roleAccess?.access_name
                              ) >= 0
                            }
                            onChange={(ev) => {
                              if (!ev.target.checked) {
                                const accesses = [...form.accesses];
                                setForm({
                                  ...form,
                                  accesses: accesses.filter(
                                    (x) =>
                                      x.access_name !== roleAccess?.access_name
                                  )
                                });
                                return;
                              }

                              const accesses = [...form.accesses];
                              accesses.push(roleAccess);
                              setForm({
                                ...form,
                                accesses
                              });
                            }}
                            id={`role-access--${roleAccess?.id}`}
                          />
                          <label htmlFor={`role-access--${roleAccess?.id}`}>
                            {roleAccess?.access_name}
                          </label>
                        </div>
                      );
                    })}
                </div>
              </label>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleCreateEditRole}
                >
                  {form.id ? 'Edit' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      {/* Delete Section */}
      <Transition
        show={showDelete}
        enter="transition-all ease-in duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0">
              <div className="absolute inset-0 bg-gray-100 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

            <div
              className="inline-block p-8 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <h3 className="font-bold">Delete Confirmation</h3>
              <div
                className="absolute cursor-pointer top-3 right-3"
                onClick={closeDelete}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p>Are you sure to delete this item?</p>
              <div className="flex flex-row-reverse">
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center text-white border border-transparent rounded-lg leading-5 transition-colors duration-150 bg-secondary hover:bg-yellow-700 focus:outline-none focus:shadow-outline-purple"
                  onClick={closeDelete}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 mt-3 text-sm font-medium text-center border border-transparent rounded-lg leading-5 transition-colors duration-150 focus:outline-none focus:shadow-outline-purple"
                  onClick={handleDeleteRole}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <Notification
        notification={notification}
        closeNotification={closeNotification}
      />
    </div>
  );
}
